import React, { useState, useEffect, useRef } from 'react';
import { X, Play, Clock, Book, BookOpen, Video, ChevronRight } from 'lucide-react';
import API_URL from '../config/API_URL';
import { Button } from '../components/Button';

export const ViewRecordedStoriesModal = ({ isOpen, onClose, readerId, readerName }) => {
  const [recordings, setRecordings] = useState([]);
  const [personalRecordings, setPersonalRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [sectionsExpanded, setSectionsExpanded] = useState({
    libraryStories: true,
    personalStories: true
  });

  const [singleStoryViewer, setSingleStoryViewer] = useState({
    isOpen: false,
    recording: null,
    viewerMode: 'both'
  });
  const videoRefSingle = useRef(null);
  const audioRefSingle = useRef(null);

  useEffect(() => {
    if (isOpen && readerId) {
      fetchRecordings();
    }
  }, [isOpen, readerId]);

  const fetchRecordings = async () => {
    try {
      setLoading(true);
      
      // Fetch library recordings
      const response = await fetch(`${API_URL}/api/reader/${readerId}/recordings`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch recordings');
      
      const data = await response.json();
      setRecordings(data);
      
      // Fetch personal book recordings
      const personalResponse = await fetch(`${API_URL}/api/reader/${readerId}/personal-recordings`, {
        credentials: 'include'
      });
      
      if (personalResponse.ok) {
        const personalData = await personalResponse.json();
        setPersonalRecordings(personalData);
      }
    } catch (error) {
      console.error('Error fetching recordings:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setSectionsExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleOpenSingleStory = (recording, isPersonal = false) => {
    let viewerMode = 'both';
    
    if (recording.is_ai) {
      viewerMode = 'book';
    } else if (recording.video_url && !recording.audio_url) {
      viewerMode = 'video';
    } else if (recording.audio_url && !recording.video_url) {
      viewerMode = 'book';
    } else if (recording.video_url && recording.audio_url) {
      viewerMode = 'both';
    }
    
    setSingleStoryViewer({
      isOpen: true,
      recording: { ...recording, isPersonal },
      viewerMode: viewerMode
    });
  };

  const closeSingleStoryViewer = () => {
    setSingleStoryViewer({
      isOpen: false,
      recording: null,
      viewerMode: 'both'
    });
    
    if (videoRefSingle.current) {
      videoRefSingle.current.pause();
    }
    if (audioRefSingle.current) {
      audioRefSingle.current.pause();
    }
  };

  const handleSingleStoryEnded = () => {
    console.log('Story finished!');
  };

  if (!isOpen) return null;

  // If viewer is open, show that instead
  if (singleStoryViewer.isOpen && singleStoryViewer.recording) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 2000
      }}>
        {/* Header */}
        <div style={{
          background: singleStoryViewer.recording.isPersonal 
            ? 'linear-gradient(135deg, #10b981, #059669)'
            : singleStoryViewer.recording.is_ai 
            ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' 
            : 'rgba(0, 0, 0, 0.8)',
          padding: '1rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>
              {singleStoryViewer.recording.book?.title || singleStoryViewer.recording.book_title || 'Untitled Story'}
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#e5e7eb' }}>
              {singleStoryViewer.recording.isPersonal 
                ? `Personal Book • Read by ${readerName}`
                : singleStoryViewer.recording.is_ai 
                ? `AI Voice: ${singleStoryViewer.recording.voice_name} (Original: ${readerName})`
                : `Read by ${readerName}`
              }
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* View mode buttons - only show for video recordings */}
            {!singleStoryViewer.recording.is_ai && singleStoryViewer.recording.video_url && !singleStoryViewer.recording.isPersonal && (
              <>
                <Button
                  onClick={() => setSingleStoryViewer(prev => ({ ...prev, viewerMode: 'video' }))}
                  variant={singleStoryViewer.viewerMode === 'video' ? 'primary' : 'ghost'}
                  style={{
                    background: singleStoryViewer.viewerMode === 'video' ? '#db2777' : 'transparent',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem'
                  }}
                >
                  <Video size={16} /> Video Only
                </Button>
                <Button
                  onClick={() => setSingleStoryViewer(prev => ({ ...prev, viewerMode: 'both' }))}
                  variant={singleStoryViewer.viewerMode === 'both' ? 'primary' : 'ghost'}
                  style={{
                    background: singleStoryViewer.viewerMode === 'both' ? '#db2777' : 'transparent',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem'
                  }}
                >
                  <BookOpen size={16} /> <Video size={16} /> Both
                </Button>
              </>
            )}
            <Button
              onClick={closeSingleStoryViewer}
              variant="ghost"
              style={{ color: 'white', fontSize: '1.5rem', padding: '0.5rem 1rem' }}
            >
              <X size={24} />
            </Button>
          </div>
        </div>

        {/* Content Area */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          gap: '1rem', 
          padding: '1rem',
          overflow: 'hidden'
        }}>
          {/* Personal Book Recording Section */}
          {singleStoryViewer.recording.isPersonal && (
            <>
              {singleStoryViewer.recording.recording_type === 'audio_only' ? (
                // Audio-only personal recording
                <div style={{ flex: 1, position: 'relative' }}>
                  {/* Book Cover Display */}
                  <div style={{ 
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                  }}>
                    {singleStoryViewer.recording.book?.cover_image_url ? (
                      <img 
                        src={singleStoryViewer.recording.book.cover_image_url}
                        alt={singleStoryViewer.recording.book?.title}
                        style={{
                          maxWidth: '400px',
                          maxHeight: '600px',
                          objectFit: 'contain',
                          borderRadius: '0.5rem',
                          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '300px',
                        height: '400px',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        borderRadius: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '6rem',
                        color: 'white',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                      }}>
                        📚
                      </div>
                    )}
                  </div>
                  
                  {/* Audio Player Overlay */}
                  <div style={{
                    position: 'absolute',
                    bottom: '2rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    padding: '1.5rem 2rem',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 32px rgba(16, 185, 129, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    minWidth: '400px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem'
                    }}>
                      🎙️
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: 'white', fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
                        Personal Audio Recording
                      </p>
                      <p style={{ color: '#e5e7eb', fontSize: '0.875rem' }}>
                        {readerName}
                      </p>
                    </div>
                    <audio
                      ref={audioRefSingle}
                      src={singleStoryViewer.recording.audio_url}
                      controls
                      autoPlay
                      onEnded={handleSingleStoryEnded}
                      style={{
                        maxWidth: '300px'
                      }}
                    />
                  </div>
                </div>
              ) : (
                // Video personal recording
                <video
                  ref={videoRefSingle}
                  src={singleStoryViewer.recording.video_url}
                  controls
                  autoPlay
                  onEnded={handleSingleStoryEnded}
                  style={{
                    width: '100%',
                    height: '100%',
                    background: '#000'
                  }}
                />
              )}
            </>
          )}

          {/* AI Audio Player Section */}
          {!singleStoryViewer.recording.isPersonal && singleStoryViewer.recording.is_ai && (
            <>
              <div style={{ 
                flex: 1,
                background: '#fff',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {singleStoryViewer.recording.book?.pdf_url ? (
                  <iframe
                    src={singleStoryViewer.recording.book.pdf_url.trim()}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                    title={singleStoryViewer.recording.book.title}
                  />
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    fontSize: '1.25rem',
                    color: '#6b7280'
                  }}>
                    No book PDF available
                  </div>
                )}
              </div>

              <div style={{
                position: 'absolute',
                bottom: '2rem',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                padding: '1.5rem 2rem',
                borderRadius: '1rem',
                boxShadow: '0 8px 32px rgba(139, 92, 246, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                minWidth: '400px',
                zIndex: 10
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>
                  🤖
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ color: 'white', fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
                    AI Audio Playing
                  </p>
                  <p style={{ color: '#e5e7eb', fontSize: '0.875rem' }}>
                    {singleStoryViewer.recording.voice_name}
                  </p>
                </div>
                <audio
                  ref={audioRefSingle}
                  src={singleStoryViewer.recording.audio_url}
                  controls
                  autoPlay
                  onEnded={handleSingleStoryEnded}
                  style={{
                    maxWidth: '300px'
                  }}
                />
              </div>
            </>
          )}

          {/* Regular Video Recording Section */}
          {!singleStoryViewer.recording.isPersonal && !singleStoryViewer.recording.is_ai && (
            <>
              {singleStoryViewer.recording.video_url && (singleStoryViewer.viewerMode === 'video' || singleStoryViewer.viewerMode === 'both') && (
                <div style={{ 
                  flex: singleStoryViewer.viewerMode === 'both' ? 1 : '1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#000'
                }}>
                  <video
                    ref={videoRefSingle}
                    src={singleStoryViewer.recording.video_url}
                    controls
                    autoPlay
                    onEnded={handleSingleStoryEnded}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </div>
              )}

              {(singleStoryViewer.viewerMode === 'book' || singleStoryViewer.viewerMode === 'both' || !singleStoryViewer.recording.video_url) && (
                <div style={{ 
                  flex: singleStoryViewer.viewerMode === 'both' ? 1 : '1',
                  background: '#fff',
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {singleStoryViewer.recording.book?.pdf_url ? (
                    <iframe
                      src={singleStoryViewer.recording.book.pdf_url.trim()}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                      title={singleStoryViewer.recording.book?.title || 'Book'}
                    />
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      fontSize: '1.25rem',
                      color: '#6b7280'
                    }}>
                      No book PDF available
                    </div>
                  )}
                </div>
              )}

              {!singleStoryViewer.recording.video_url && singleStoryViewer.recording.audio_url && (
                <div style={{
                  position: 'absolute',
                  bottom: '2rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #db2777, #be185d)',
                  padding: '1.5rem 2rem',
                  borderRadius: '1rem',
                  boxShadow: '0 8px 32px rgba(219, 39, 119, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  minWidth: '400px',
                  zIndex: 10
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem'
                  }}>
                    🎙️
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: 'white', fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
                      Audio Recording
                    </p>
                    <p style={{ color: '#e5e7eb', fontSize: '0.875rem' }}>
                      {readerName}
                    </p>
                  </div>
                  <audio
                    ref={audioRefSingle}
                    src={singleStoryViewer.recording.audio_url}
                    controls
                    autoPlay
                    onEnded={handleSingleStoryEnded}
                    style={{
                      maxWidth: '300px'
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Regular modal view with sections
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '1rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
            Stories by {readerName}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={24} color="#6b7280" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading recordings...</p>
          ) : recordings.length === 0 && personalRecordings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Book size={48} color="#d1d5db" style={{ margin: '0 auto 1rem' }} />
              <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>
                {readerName} hasn't recorded any stories yet
              </p>
            </div>
          ) : (
            <>
              {/* Library Stories Section */}
              <div style={{ marginBottom: '2rem' }}>
                <div 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    marginBottom: sectionsExpanded.libraryStories ? '1rem' : '0'
                  }}
                  onClick={() => toggleSection('libraryStories')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Book size={20} color="white" />
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'white', margin: 0 }}>
                      Library Stories
                    </h3>
                    <span style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '1rem',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {recordings.length}
                    </span>
                  </div>
                  <ChevronRight 
                    size={20} 
                    style={{ 
                      transform: sectionsExpanded.libraryStories ? 'rotate(90deg)' : 'rotate(0deg)', 
                      transition: 'transform 0.2s',
                      color: 'white'
                    }} 
                  />
                </div>

                {sectionsExpanded.libraryStories && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {recordings.length === 0 ? (
                      <p style={{ textAlign: 'center', color: '#6b7280', padding: '1rem' }}>
                        No library stories recorded yet
                      </p>
                    ) : (
                      recordings.map(recording => (
                        <div
                          key={recording.id}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.75rem',
                            padding: '1.25rem',
                            background: 'white',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                            e.currentTarget.style.borderColor = '#9333ea';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                            e.currentTarget.style.borderColor = '#e5e7eb';
                          }}
                        >
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                            {recording.book?.pdf_url ? (
                              <div style={{
                                width: '100px',
                                height: '140px',
                                flexShrink: 0,
                                border: '1px solid #e5e7eb',
                                borderRadius: '0.5rem',
                                overflow: 'hidden',
                                backgroundColor: '#f9fafb'
                              }}>
                                <iframe 
                                  src={`${recording.book.pdf_url.trim()}#page=1&view=FitH`}
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    border: 'none',
                                    pointerEvents: 'none'
                                  }}
                                  title={`${recording.title} preview`}
                                />
                              </div>
                            ) : (
                              <div style={{
                                width: '100px',
                                height: '140px',
                                flexShrink: 0,
                                border: '1px solid #e5e7eb',
                                borderRadius: '0.5rem',
                                backgroundColor: '#f9fafb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '2.5rem'
                              }}>
                                📖
                              </div>
                            )}

                            <div style={{ flex: 1 }}>
                              <h3 style={{
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                color: '#1f2937',
                                marginBottom: '0.25rem'
                              }}>
                                {recording.book?.title || 'Unknown Book'}
                              </h3>
                              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                by {recording.book?.author || 'Unknown'}
                              </p>

                              <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <Clock size={14} color="#6b7280" />
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                    {Math.floor(recording.duration_seconds / 60)}:{(recording.duration_seconds % 60).toString().padStart(2, '0')}
                                  </span>
                                </div>
                                <div style={{
                                  fontSize: '0.75rem',
                                  padding: '0.125rem 0.375rem',
                                  borderRadius: '0.25rem',
                                  backgroundColor: recording.processing_status === 'ready' ? '#d1fae5' : '#fef3c7',
                                  color: recording.processing_status === 'ready' ? '#065f46' : '#92400e'
                                }}>
                                  {recording.processing_status}
                                </div>
                              </div>

                              <button
                                onClick={() => handleOpenSingleStory(recording)}
                                style={{
                                  background: '#9333ea',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  borderRadius: '0.5rem',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.875rem',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7e22ce'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9333ea'}
                              >
                                <Play size={16} /> Watch
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Personal Stories Section */}
              <div>
                <div 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    marginBottom: sectionsExpanded.personalStories ? '1rem' : '0'
                  }}
                  onClick={() => toggleSection('personalStories')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <BookOpen size={20} color="white" />
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'white', margin: 0 }}>
                      Personal Book Stories
                    </h3>
                    <span style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '1rem',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {personalRecordings.length}
                    </span>
                  </div>
                  <ChevronRight 
                    size={20} 
                    style={{ 
                      transform: sectionsExpanded.personalStories ? 'rotate(90deg)' : 'rotate(0deg)', 
                      transition: 'transform 0.2s',
                      color: 'white'
                    }} 
                  />
                </div>

                {sectionsExpanded.personalStories && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {personalRecordings.length === 0 ? (
                      <p style={{ textAlign: 'center', color: '#6b7280', padding: '1rem' }}>
                        No personal book stories recorded yet
                      </p>
                    ) : (
                      personalRecordings.map(recording => (
                        <div
                          key={`personal-${recording.id}`}
                          style={{
                            border: '2px solid #10b981',
                            borderRadius: '0.75rem',
                            padding: '1.25rem',
                            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s',
                            cursor: 'pointer',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(16, 185, 129, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                          }}
                        >
                          <div style={{
                            position: 'absolute',
                            top: '0.75rem',
                            right: '0.75rem',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            <BookOpen size={12} />
                            PERSONAL
                          </div>

                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                            {recording.book?.cover_image_url ? (
                              <img 
                                src={recording.book.cover_image_url}
                                alt={recording.book?.title}
                                style={{
                                  width: '100px',
                                  height: '140px',
                                  objectFit: 'cover',
                                  borderRadius: '0.5rem',
                                  border: '2px solid #10b981',
                                  flexShrink: 0
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '100px',
                                height: '140px',
                                flexShrink: 0,
                                borderRadius: '0.5rem',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '2.5rem',
                                color: 'white'
                              }}>
                                📚
                              </div>
                            )}

                            <div style={{ flex: 1, paddingRight: '3rem' }}>
                              <h3 style={{
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                color: '#064e3b',
                                marginBottom: '0.25rem'
                              }}>
                                {recording.book?.title || 'Untitled Book'}
                              </h3>
                              <p style={{ fontSize: '0.875rem', color: '#047857', marginBottom: '0.5rem' }}>
                                by {recording.book?.author || 'Unknown'}
                              </p>

                              {/* <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  {recording.recording_type === 'audio_only' ? '🎙️' : '📹'}
                                  <span style={{ fontSize: '0.75rem', color: '#065f46' }}>
                                    {recording.recording_type === 'audio_only' ? 'Audio' : 'Video'}
                                  </span>
                                </div>
                                {recording.duration_seconds && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <Clock size={14} color="#065f46" />
                                    <span style={{ fontSize: '0.75rem', color: '#065f46' }}>
                                      {Math.floor(recording.duration_seconds / 60)}:{(recording.duration_seconds % 60).toString().padStart(2, '0')}
                                    </span>
                                  </div>
                                )}
                              </div> */}

                              {/* {recording.kids && recording.kids.length > 0 && (
                                <p style={{ fontSize: '0.75rem', color: '#065f46', marginBottom: '0.5rem' }}>
                                  Assigned to: {recording.kids.map(k => k.name).join(', ')}
                                </p>
                              )} */}

                              <button
                                onClick={() => handleOpenSingleStory(recording, true)}
                                style={{
                                  background: 'linear-gradient(135deg, #10b981, #059669)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  borderRadius: '0.5rem',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.875rem',
                                  transition: 'transform 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              >
                                <Play size={16} /> {recording.recording_type === 'audio_only' ? 'Listen' : 'Watch'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};